/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { BaseLegendProps, LegendItem } from './Legend.type';
import { BaseLegendItem } from './BaseLegendItem';
import { getItemProps, getGridCellIndices } from './LegendUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { styles } from './themes/LegendStyles.css';

/**
 * The controlled legend reponsible only for rendering the legend items.
 */
export const BaseLegend = <K,>({
  orientation = 'horizontal',
  highlightedIds,
  hiddenIds,
  focusedItemInfo,
  hoveredItemInfo,
  sectionIdx,
  activeId,
  isReadOnly,
  items,
  symbolHeight,
  symbolWidth,
  ...props
}: BaseLegendProps<K>) => {
  const isHoriz = orientation === 'horizontal';
  const isColumnDefined = props.isColumnWidthValidRef.current;
  const columTemplates =
    isColumnDefined && isHoriz ? `repeat(${props.itemsPerRow}, ${props.itemWidth})` : undefined;
  props.isColumnWidthValidRef.current = false;
  const {
    baseLegendStyles,
    baseLegendHorizontal,
    baseLegendVertical,
    baseLegendVerticalItem,
    baseLegendPaddingBottom,
    baseLegendPaddingTop,
    baseLegendPaddingEnd
  } = styles;

  return (
    <div
      className={classNames([
        baseLegendStyles,
        isHoriz ? baseLegendHorizontal : baseLegendVertical
      ])}
      style={{
        gridTemplateColumns: columTemplates,
        // set maxWidth to 100% on second render for text truncation to work.
        maxWidth: isColumnDefined ? '100%' : ''
      }}>
      {items.map((item: LegendItem<K>, index: number) => {
        const itemProps = getItemProps(
          item,
          index,
          sectionIdx,
          focusedItemInfo,
          isReadOnly,
          hoveredItemInfo,
          hiddenIds,
          highlightedIds
        );
        const isFocusedItem =
          focusedItemInfo.sectionIdx === sectionIdx && focusedItemInfo.itemIdx === index;
        const { gridRow, gridCol } = getGridCellIndices(
          items.length,
          index,
          props.itemsPerRow,
          props.itemsPerCol
        );
        const needsBottomSpace = gridRow != null && gridRow != props.itemsPerCol;
        const needsTopSpace = gridRow != null && gridRow != 1;
        return (
          <BaseLegendItem
            ref={isFocusedItem ? props.focusedItemRef : undefined}
            itemIdx={index}
            sectionIdx={sectionIdx}
            symbolHeight={symbolHeight}
            symbolWidth={symbolWidth}
            {...props}
            {...itemProps}
            gridRow={gridRow}
            gridCol={gridCol}
            minWidth={props.itemWidth}
            id={itemProps.isCurrent ? activeId : ''}
            class={classNames([
              isHoriz ? '' : baseLegendVerticalItem,
              needsTopSpace ? baseLegendPaddingTop : '',
              needsBottomSpace ? baseLegendPaddingBottom : '',
              baseLegendPaddingEnd
            ])}
          />
        );
      })}
    </div>
  );
};
